/**
 * useApiPagedFetch — la LECTURE paginée des ports (patron A : le réducteur range les pages, page 1
 * remplace, la suite s'ajoute), posée sur `useApiFetch`.
 *
 * Le curseur se recopie de la `meta` de NOTRE réponse : `scopeUrlParams` (la page s'y ajoute
 * toujours) dit pour qui la lecture est partie, et une réponse d'une autre page, d'une recherche
 * changée ou d'une autre surface qui lit la même tranche ne le bouge pas. Une seule lecture à la fois :
 * relire la page 1 déjà en route, avec les mêmes paramètres, ne repart pas ; de nouveaux paramètres
 * (une recherche, un onglet) la remplacent, et la réponse de l'ancienne n'est plus lue.
 *
 * Une page qui échoue se réessaie telle quelle par `loadMore`. La tranche, elle, garde son erreur et
 * son état d'attente : un view-model peut y lire le chargement (patron A).
 */
import { useCallback, useEffect, useRef, useState } from "react"

import { type ApiService, useApiFetch } from "./useApiFetch"

export type ApiPageMeta = { current_page?: number | null; last_page?: number | null; total?: number | null }

type PagedAnswer = { meta?: ApiPageMeta | null }

type UrlParams = Record<string, unknown>

type Cursor = {
    page: number
    lastPage: number
    total: number
    /** La page demandée, en route. */
    inFlight: number | null
    /** La page dont la lecture a échoué. */
    failed: number | null
    /** Une page de CETTE lecture est arrivée (depuis la dernière relecture). */
    answered: boolean
}

const fresh = (inFlight: number | null): Cursor => ({
    page: 1,
    lastPage: 1,
    total: 0,
    inFlight,
    failed: null,
    answered: false,
})

export type UseApiPagedFetchOptions = {
    service: ApiService
    /** Les paramètres d'URL de la première lecture, hors page (une recherche, un onglet). */
    urlParams?: UrlParams
    /** Les paramètres d'URL, hors page, qui disent POUR QUI la lecture est partie. */
    scopeUrlParams?: string[]
    /** Défaut `true` : la page 1 part au montage. */
    fetchOnMount?: boolean
}

const sameParams = (a: UrlParams, b: UrlParams) => {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)])
    return [...keys].every((key) => String(a[key] ?? "") === String(b[key] ?? ""))
}

export function useApiPagedFetch({
    service,
    urlParams,
    scopeUrlParams = [],
    fetchOnMount = true,
}: UseApiPagedFetchOptions) {
    const [cursor, setCursor] = useState<Cursor>(() => fresh(fetchOnMount ? 1 : null))
    /** La vérité des gestes : l'état ne sert qu'au rendu, un geste lit la page en route ici. */
    const cursorRef = useRef(cursor)
    const update = useCallback((next: (current: Cursor) => Cursor) => {
        cursorRef.current = next(cursorRef.current)
        setCursor(cursorRef.current)
    }, [])
    const paramsRef = useRef<UrlParams>(urlParams ?? {})

    const list = useApiFetch<PagedAnswer>({
        service,
        fetchOnMount: false,
        scopeUrlParams: [...scopeUrlParams, "page"],
        onSuccess: (answer) =>
            update((current) => ({
                page: answer?.meta?.current_page ?? current.inFlight ?? current.page,
                lastPage: answer?.meta?.last_page ?? current.lastPage,
                total: answer?.meta?.total ?? current.total,
                inFlight: null,
                failed: null,
                answered: true,
            })),
        onError: () => update((current) => ({ ...current, failed: current.inFlight, inFlight: null })),
    })
    const { fetchWith, cancel } = list

    /** La page 1, sur `next` (sinon les paramètres de la dernière lecture). */
    const refresh = useCallback(
        (next?: UrlParams) => {
            const params = next ?? paramsRef.current
            if (cursorRef.current.inFlight === 1 && sameParams(params, paramsRef.current)) return
            paramsRef.current = params
            update(() => fresh(1))
            fetchWith({ ...params, page: 1 })
        },
        [fetchWith, update]
    )

    const loadMore = useCallback(() => {
        const current = cursorRef.current
        if (current.inFlight != null) return
        // Après un échec, on réessaie la même page.
        const next = current.failed ?? current.page + 1
        if (current.failed == null && !(current.answered && current.page < current.lastPage)) return
        update((now) => ({ ...now, inFlight: next, failed: null }))
        fetchWith({ ...paramsRef.current, page: next })
    }, [fetchWith, update])

    /** Coupe la lecture en route : sa réponse ne bougera pas le curseur. */
    const stop = useCallback(() => {
        cancel()
        update((current) => ({ ...current, inFlight: null }))
    }, [cancel, update])

    useEffect(() => {
        if (fetchOnMount) fetchWith({ ...paramsRef.current, page: 1 })
        // Le montage seulement : la suite passe par `refresh`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return {
        refresh,
        loadMore,
        cancel: stop,
        answered: cursor.answered,
        hasMore: cursor.answered && cursor.page < cursor.lastPage,
        totalCount: cursor.total,
        /** La page 1 est en route (l'ouverture, une relecture, une nouvelle recherche). */
        firstPageLoading: cursor.inFlight === 1,
        loadingMore: cursor.inFlight != null && cursor.inFlight > 1,
        /** La suite a échoué : `loadMore` la réessaie. */
        loadMoreFailed: cursor.failed != null && cursor.failed > 1,
        /** La première page a échoué. */
        firstPageFailed: cursor.failed === 1,
    }
}
